import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, BookOpen } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import SubmitReviewForm from "@/components/SubmitReviewForm";
import Seo from "@/components/Seo";
import ShareButton from "@/components/ShareButton";

export default function Book() {
  const [book, setBook] = useState(null);
  useEffect(() => {
    api.get("/book").then((r) => setBook(r.data)).catch(() => setBook({}));
  }, []);

  if (!book) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32" data-testid="book-loading">
        <p className="text-ink-soft">Loading…</p>
      </div>
    );
  }

  const links = (book.purchase_links || []).filter((l) => l.url && l.label);
  const endorsements = (book.endorsements || []).filter(
    (e) => e.quote && e.reviewer_name,
  );

  return (
    <div className="bg-paper" data-testid="book-page">
      <Seo
        title={book.title || "The Book"}
        description={book.tagline || book.description || "A children's book by Barbara Ruffing."}
        image={resolveImage(book.cover_image)}
        type="book"
      />
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 rise-in">
            {book.cover_image ? (
              <img
                src={resolveImage(book.cover_image)}
                alt={book.title}
                data-testid="book-cover"
                className="w-full h-[560px] object-cover shadow-[0_30px_80px_-40px_rgba(44,42,40,0.35)]"
              />
            ) : (
              <div className="w-full h-[560px] bg-paper-2 border border-rule" />
            )}
          </div>
          <div className="lg:col-span-7 rise-in rise-delay-1">
            <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-6 inline-flex items-center gap-2">
              <BookOpen size={14} /> The Book
            </p>
            <h1
              data-testid="book-title"
              className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight leading-[1.02]"
            >
              {book.title}
            </h1>
            {book.subtitle && (
              <p
                data-testid="book-subtitle"
                className="mt-4 font-serif-display italic text-2xl text-ink-soft"
              >
                {book.subtitle}
              </p>
            )}

            {book.tagline && (
              <p
                data-testid="book-tagline"
                className="mt-8 text-lg text-ink leading-relaxed border-l-2 border-terracotta pl-5 font-serif-display italic"
              >
                {book.tagline}
              </p>
            )}

            {book.description && (
              <div
                data-testid="book-description"
                className="prose-editorial mt-8 max-w-xl whitespace-pre-wrap"
              >
                {book.description}
              </div>
            )}

            <div className="mt-10 flex flex-wrap gap-3" data-testid="book-purchase-links">
              {links.length === 0 ? (
                <p className="text-sm text-ink-soft italic">
                  Purchase links coming soon.
                </p>
              ) : (
                links.map((l, i) => (
                  <a
                    key={i}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`book-buy-link-${i}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-terracotta text-paper text-xs tracking-[0.2em] uppercase hover:bg-ink transition-colors"
                  >
                    Buy on {l.label} <ExternalLink size={13} />
                  </a>
                ))
              )}
              {book.preview_url && (
                <a
                  href={book.preview_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-preview-link"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-ink text-ink text-xs tracking-[0.2em] uppercase hover:bg-ink hover:text-paper transition-colors"
                >
                  Preview <ExternalLink size={13} />
                </a>
              )}
              <ShareButton
                testId="book-share-btn"
                title={book.title || "A children's book by Barbara Ruffing"}
                excerpt={book.tagline || book.description || "A children's book by Barbara Ruffing."}
                label="Share the book"
                url={typeof window !== "undefined" ? `${window.location.origin}/api/share/book` : undefined}
                className="inline-flex items-center gap-2 px-6 py-3 border border-ink text-ink text-xs tracking-[0.2em] uppercase hover:bg-ink hover:text-paper transition-colors"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-rule bg-paper-2/40">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20 text-center">
          <p className="font-serif-display text-3xl lg:text-4xl italic text-ink leading-relaxed">
            "Every child holds a light worth nurturing."
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.22em] text-ink-soft">
            — Barbara Ruffing
          </p>
          <Link
            to="/writings?category=childrens_books"
            className="mt-10 inline-block text-xs uppercase tracking-[0.2em] text-terracotta link-underline"
            data-testid="book-related-writings"
          >
            Read related writings
          </Link>
        </div>
      </section>

      {endorsements.length > 0 && (
        <section className="bg-paper" data-testid="endorsements-section">
          <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24">
            <div className="text-center mb-16">
              <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-4">
                Praise
              </p>
              <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight text-ink">
                What readers are <span className="italic">saying</span>
              </h2>
            </div>

            <div
              className={`grid gap-10 ${
                endorsements.length === 1
                  ? "grid-cols-1 max-w-2xl mx-auto"
                  : "grid-cols-1 md:grid-cols-2"
              }`}
            >
              {endorsements.map((e, i) => (
                <figure
                  key={i}
                  data-testid={`endorsement-${i}`}
                  className="relative border-l-2 border-terracotta pl-6 py-2"
                >
                  <span
                    aria-hidden
                    className="absolute -top-6 -left-1 font-serif-display text-7xl leading-none text-terracotta/30 select-none"
                  >
                    “
                  </span>
                  <blockquote className="font-serif-display text-2xl lg:text-[1.65rem] italic text-ink leading-snug">
                    {e.quote}
                  </blockquote>
                  <figcaption className="mt-5 text-xs uppercase tracking-[0.2em] text-ink-soft">
                    <span
                      className="text-ink"
                      data-testid={`endorsement-reviewer-${i}`}
                    >
                      — {e.reviewer_name}
                    </span>
                    {e.reviewer_title && (
                      <span className="ml-2 normal-case tracking-normal text-ink-soft italic">
                        · {e.reviewer_title}
                      </span>
                    )}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-t border-rule bg-paper-2/40" data-testid="submit-review-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.22em] text-sage mb-4">
              Share your words
            </p>
            <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight text-ink">
              Did the book <span className="italic">touch you?</span>
            </h2>
            <p className="mt-4 text-ink-soft max-w-xl mx-auto">
              Send Barbara a few honest sentences. She reads every one — and may
              feature it here.
            </p>
          </div>
          <SubmitReviewForm />
        </div>
      </section>
    </div>
  );
}
